import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  User as UserIcon,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { checkPassword } from '../lib/passwordPolicy';
import { translateAuthError, type TranslatedAuthError } from '../lib/authErrors';
import {
  TUTORIAL_FORCE_KEY,
  TUTORIAL_STORAGE_KEY,
} from '../components/tutorial/steps';
import { Button } from '../components/ui/Button';
import { Logo, LogoMark } from '../components/ui/Logo';
import { PasswordStrengthMeter } from '../components/ui/PasswordStrengthMeter';
import { StickerCard } from '../components/ui/StickerCard';
import { CMYKMark } from '../components/landing/CMYKMark';

type Mode = 'signin' | 'signup';

const ROTATING_QUOTES = [
  '“Tenho o Vini, troca a Argentina?”',
  '“Faltam só os goleiros. Bora?”',
  '“Repetida do Messi, alguém precisa?”',
  '“Domingo é dia de fechar uma seleção.”',
  '“No grupo do Zap não dá mais. Vem pro app.”',
];

const MOSAIC_STICKERS: { code: string; count: number; team: string; floatClass: string }[] = [
  { code: 'BRA10', count: 1, team: 'BRA', floatClass: 'animate-float-0' },
  { code: 'ARG10', count: 2, team: 'ARG', floatClass: 'animate-float-1' },
  { code: 'FRA9', count: 0, team: 'FRA', floatClass: 'animate-float-2' },
  { code: 'POR7', count: 1, team: 'POR', floatClass: 'animate-float-1' },
  { code: 'GER19', count: 0, team: 'GER', floatClass: 'animate-float-0' },
  { code: 'ENG10', count: 1, team: 'ENG', floatClass: 'animate-float-2' },
  { code: 'JPN8', count: 2, team: 'JPN', floatClass: 'animate-float-0' },
  { code: 'NED14', count: 0, team: 'NED', floatClass: 'animate-float-1' },
  { code: 'COL10', count: 1, team: 'COL', floatClass: 'animate-float-2' },
];

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const initialMode: Mode = searchParams.get('mode') === 'signup' ? 'signup' : 'signin';
  const [mode, setMode] = useState<Mode>(initialMode);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<TranslatedAuthError | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [quoteIdx, setQuoteIdx] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setQuoteIdx((i) => (i + 1) % ROTATING_QUOTES.length);
    }, 4500);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    setMode(searchParams.get('mode') === 'signup' ? 'signup' : 'signin');
  }, [searchParams]);

  const switchMode = (m: Mode) => {
    setMode(m);
    setError(null);
    setSuccess(null);
  };

  const passwordCheck = checkPassword(password, email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === 'signup' && !passwordCheck.ok) {
      setError({ message: 'A senha deve atingir o nível Forte (verde) para criar a conta.' });
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    if (mode === 'signin') {
      const { error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) setError(translateAuthError(err, 'signin'));
    } else {
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const { error: err } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: window.location.origin,
        },
      });
      if (err) {
        setError(translateAuthError(err, 'signup'));
      } else {
        try {
          localStorage.setItem(TUTORIAL_FORCE_KEY, '1');
          localStorage.removeItem(TUTORIAL_STORAGE_KEY);
        } catch {
          /* ignore */
        }
        setSuccess('Conta criada com sucesso. Verifique seu e-mail para confirmar o cadastro.');
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-paper">
      {/* Left pane: form */}
      <div className="relative flex flex-col bt-paper-grain">
        <div className="px-6 md:px-10 pt-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-small font-semibold text-ink-500 hover:text-ink-900 transition-colors btn-focus rounded-md py-1"
          >
            <ArrowLeft size={16} />
            Voltar
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 md:px-10 py-8">
          <div className="w-full max-w-md">
            <div className="mb-7">
              <Logo variant="on-light" size="lg" markSize={32} />
            </div>

            <p className="bt-eyebrow text-green-700 mb-2">
              {mode === 'signin' ? 'De volta' : 'Primeira vez'}
            </p>
            <h1 className="font-display font-bold text-h1 text-ink-900 leading-tight tracking-[-0.02em]">
              {mode === 'signin' ? 'E aí, pronto pra trocar?' : 'Bora começar seu álbum.'}
            </h1>
            <p className="font-body text-body text-ink-700 mt-2">
              {mode === 'signin'
                ? 'Entra com o seu e-mail e senha pra ver o álbum.'
                : 'Cria uma conta — leva 30 segundos e é grátis.'}
            </p>

            {/* Mode switch */}
            <div className="inline-flex p-0.5 bg-ink-100 rounded-pill mt-6 mb-5">
              {(['signin', 'signup'] as Mode[]).map((m) => {
                const active = mode === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => switchMode(m)}
                    className={[
                      'px-4 py-2 rounded-pill text-small font-semibold font-body transition-colors btn-focus',
                      active
                        ? 'bg-green-700 text-white shadow-xs'
                        : 'text-ink-500 hover:text-ink-900',
                    ].join(' ')}
                  >
                    {m === 'signin' ? 'Entrar' : 'Criar conta'}
                  </button>
                );
              })}
            </div>

            {error && (
              <div className="bg-ruby-100 border border-ruby-500 text-ruby-700 text-small rounded-lg p-3 mb-4 inline-flex items-start gap-2">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>
                  {error.message}
                  {error.suggestSignin && (
                    <>
                      {' '}
                      <button
                        type="button"
                        onClick={() => switchMode('signin')}
                        className="font-semibold underline btn-focus rounded-sm"
                      >
                        Faça login para continuar.
                      </button>
                    </>
                  )}
                </span>
              </div>
            )}
            {success && (
              <div className="bg-green-100 border border-green-700 text-green-900 text-small rounded-lg p-3 mb-4 inline-flex items-start gap-2">
                <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
                <span>{success}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'signup' && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label htmlFor="fname" className="block bt-eyebrow text-ink-500 mb-1.5">
                        Nome
                      </label>
                      <div className="relative">
                        <UserIcon
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500"
                        />
                        <input
                          id="fname"
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          required
                          placeholder="João"
                          className="w-full h-11 pl-9 pr-3 rounded-lg border border-ink-300 bg-white text-small text-ink-900 placeholder:text-ink-500 transition-[border-color,box-shadow] focus:outline-none focus:border-green-700 focus-visible:shadow-focus-ring"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="lname" className="block bt-eyebrow text-ink-500 mb-1.5">
                        Sobrenome
                      </label>
                      <input
                        id="lname"
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        placeholder="Silva"
                        className="w-full h-11 px-3 rounded-lg border border-ink-300 bg-white text-small text-ink-900 placeholder:text-ink-500 transition-[border-color,box-shadow] focus:outline-none focus:border-green-700 focus-visible:shadow-focus-ring"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label htmlFor="email" className="block bt-eyebrow text-ink-500 mb-1.5">
                  E-mail
                </label>
                <div className="relative">
                  <Mail
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500"
                  />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="seu@email.com"
                    autoComplete="email"
                    className="w-full h-11 pl-9 pr-3 rounded-lg border border-ink-300 bg-white text-small text-ink-900 placeholder:text-ink-500 transition-[border-color,box-shadow] focus:outline-none focus:border-green-700 focus-visible:shadow-focus-ring"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="pwd" className="block bt-eyebrow text-ink-500 mb-1.5">
                  Senha
                </label>
                <div className="relative">
                  <Lock
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500"
                  />
                  <input
                    id="pwd"
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={mode === 'signup' ? 10 : undefined}
                    placeholder="••••••••"
                    autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                    className="w-full h-11 pl-9 pr-10 rounded-lg border border-ink-300 bg-white text-small text-ink-900 placeholder:text-ink-500 transition-[border-color,box-shadow] focus:outline-none focus:border-green-700 focus-visible:shadow-focus-ring"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? 'Esconder senha' : 'Mostrar senha'}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-900 p-1 rounded-md btn-focus"
                  >
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {mode === 'signup' && <PasswordStrengthMeter check={passwordCheck} />}
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                iconRight={ArrowRight}
                loading={loading}
                disabled={mode === 'signup' && !passwordCheck.ok}
              >
                {mode === 'signin' ? 'Entrar' : 'Criar conta'}
              </Button>
            </form>

            {mode === 'signin' && (
              <p className="text-center mt-3">
                <Link
                  to="/esqueci-senha"
                  className="text-caption font-semibold text-green-700 hover:text-green-900 underline btn-focus rounded-sm"
                >
                  Esqueci a senha
                </Link>
              </p>
            )}

            <p className="text-center text-caption text-ink-500 mt-5">
              {mode === 'signin' ? (
                <>
                  Ainda não tem conta?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('signup')}
                    className="font-semibold text-green-700 underline btn-focus rounded-sm"
                  >
                    Cria uma agora.
                  </button>
                </>
              ) : (
                <>
                  Já tem conta?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('signin')}
                    className="font-semibold text-green-700 underline btn-focus rounded-sm"
                  >
                    Entrar.
                  </button>
                </>
              )}
            </p>
          </div>
        </div>

        <div className="px-6 md:px-10 pb-6 flex items-center justify-between text-caption text-ink-500">
          <span className="bt-eyebrow text-ink-500/70">BoraTrocar</span>
          <CMYKMark className="opacity-30" />
        </div>
      </div>

      {/* Right pane: mosaic + quotes */}
      <div className="hidden md:flex relative bg-green-700 text-white overflow-hidden items-center justify-center">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle at 70% 30%, rgba(241,196,15,0.18), transparent 55%)',
          }}
        />

        <div className="absolute top-6 left-6 inline-flex items-center gap-2 text-caption text-white/65">
          <LogoMark size={20} />
          <span className="bt-eyebrow text-white/65">Mosaico · 9 figurinhas</span>
        </div>
        <div className="absolute top-6 right-6 bt-eyebrow text-gold-500/85">
          Copa · 2026
        </div>

        <div
          className="grid gap-4 p-10"
          style={{ gridTemplateColumns: 'repeat(3, 72px)' }}
        >
          {MOSAIC_STICKERS.map((s) => (
            <div key={s.code} className={s.floatClass}>
              <StickerCard code={s.code} count={s.count} size="lg" />
            </div>
          ))}
        </div>

        <div className="absolute bottom-10 left-6 right-6">
          <div className="bg-white/10 border border-white/15 rounded-lg backdrop-blur-sm p-4">
            <p
              key={quoteIdx}
              className="font-display font-semibold text-h3 text-white animate-fade-up"
            >
              {ROTATING_QUOTES[quoteIdx]}
            </p>
            <div className="flex items-center gap-1.5 mt-3">
              {ROTATING_QUOTES.map((_, i) => (
                <span
                  key={i}
                  className={[
                    'h-1 rounded-full transition-all duration-base',
                    i === quoteIdx ? 'w-6 bg-gold-500' : 'w-1.5 bg-white/30',
                  ].join(' ')}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
